export const IncidentModel = {
  externalId: "",
  sourceId: "",
  locationDescription: "",
  description: "",
  comments: "",
  sourceName: "",
  locationResolutionStatus: "",
  state: "",
  confidenceLevel: "",
  severity: "",
  roadwayCondition: "",
  weather: "",
  detectionType: "",
  verificationType: "",
  automobileCount: 0,
  busCount: 0,
  motorcycleCount: 0,
  pickupTruckCount: 0,
  semiTrailerCount: 0,
  tankerTruckCount: 0,
  fatalityCount: 0,
  injuryCount: 0,
  accident: false,
  fire: false,
  hazmat: false,
  policeAction: false,
  medicalEmergency: false,
  occurrenceTime: null,
  detectionTime: null,
  verificationTime: null,
  // estimatedClosureTime: null,
  isDurationOption: false,
  durationMinutes: 0,
  locations: [],
  maxLaneCount: 0,
  laneImpacts: [],
  leftShoulder: "",
  rightShoulder: "",
  unspecifiedShoulder: "",
  laneType: "",
  fullClosure: false,
  variousLanes: false,
  userLaneCount: 0,
  resolvedLocation: null,
  createUnresolved: false,
};

export const getAllIncidents = async (
  setIncidents,
  setAuthenticationChecked,
) => {
  const endpoint = "/admin/incidentEntry/incidents.json";
  try {
    const res = await fetch(process.env.REACT_APP_API_HOST + endpoint, {
      method: "GET",
    });
    res.json().then((res) => {
      setIncidents(res.data);
    });
    if (res.success === true) {
      setAuthenticationChecked("Success");
    } else if (
      res.error !== undefined &&
      res.error !== null &&
      res.error.code === "UNAUTHORIZED"
    ) {
      setAuthenticationChecked("UNAUTHORIZED");
    } else if (
      res.error !== undefined &&
      res.error !== null &&
      res.error.code === "FORBIDDEN"
    ) {
      setAuthenticationChecked("FORBIDDEN");
    }
    if (!res.ok) {
      if (res.status === 403) {
        setAuthenticationChecked("FORBIDDEN");
      }
      throw new Error(`Error ${res.status} : ${res.statusText}`);
    }
  } catch (err) {
    if (err.message.indexOf("401") > -1) {
      setAuthenticationChecked("UNAUTHORIZED");
    } else if (err.message.indexOf("403") > -1) {
      setAuthenticationChecked("FORBIDDEN");
    }
  }
};

export const getIncident = async (
  incidentId,
  setIncident,
  setErrorDialog = null,
) => {
  const endpoint = "/admin/incidentEntry/" + incidentId + ".json";
  try {
    const res = await fetch(process.env.REACT_APP_API_HOST + endpoint, {
      method: "GET",
    });

    if (!res.ok) {
      if (res.status === 404) {
        const errorMessage = `Incident with ID ${incidentId} was not found.`;
        if (setErrorDialog) {
          setErrorDialog({
            open: true,
            message: errorMessage,
          });
        } else {
          alert(errorMessage);
        }
        setIncident(null);
        return;
      }
      throw new Error(`Error ${res.status} : ${res.statusText}`);
    }

    const text = await res.text();
    if (!text || text.trim() === "") {
      throw new Error("Server returned an empty response");
    }
    const responseData = JSON.parse(text);
    setIncident(responseData.data);
  } catch (err) {
    if (err.message.includes("404")) {
      const errorMessage = `Incident with ID ${incidentId} was not found.`;
      if (setErrorDialog) {
        setErrorDialog({
          open: true,
          message: errorMessage,
        });
      } else {
        alert(errorMessage);
      }
      setIncident(null);
    } else {
      const errorMessage = `Error loading incident: ${err.message}`;
      if (setErrorDialog) {
        setErrorDialog({
          open: true,
          title: "Error Loading Incident",
          message: errorMessage,
        });
      } else {
        alert(errorMessage);
      }
    }
  }
};

export const getDropdowns = async (setDropdowns) => {
  const endpoint = "/admin/incidentEntry/dropdowns.json";
  try {
    const res = await fetch(process.env.REACT_APP_API_HOST + endpoint, {
      method: "GET",
    });
    res.json().then((res) => {
      setDropdowns(res.data);
    });
    if (!res.ok) {
      throw new Error(`Error ${res.status} : ${res.statusText}`);
    }
  } catch (err) {}
};

export const resolveLocation = async (incidentId, locationData) => {
  const endpoint = "/admin/incidentEntry/resolve.json";
  try {
    const res = await fetch(process.env.REACT_APP_API_HOST + endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(locationData),
    });

    const responseData = await res.json();

    if (responseData.success) {
      return responseData;
    } else {
      throw new Error(
        responseData.error?.message || "Location resolution failed",
      );
    }
  } catch (err) {
    throw new Error("Something went wrong: " + err.message);
  }
};

export const getNewIncident = async (setNewIncident, setErrorDialog = null) => {
  const endpoint = "/admin/incidentEntry/new.json";
  try {
    const res = await fetch(process.env.REACT_APP_API_HOST + endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!res.ok) {
      throw new Error(`Error ${res.status}: ${res.statusText}`);
    }

    const responseData = await res.json();

    if (responseData.success && responseData.data) {
      const newIncidentData = {
        ...IncidentModel,
        ...responseData.data,
      };
      setNewIncident(newIncidentData);
    } else {
      const errorMessage =
        responseData.error?.message || "Failed to load new incident data";
      if (setErrorDialog) {
        setErrorDialog({
          open: true,
          message: errorMessage,
        });
      } else {
        alert(errorMessage);
      }
      setNewIncident({ ...IncidentModel });
    }
  } catch (err) {
    const errorMessage = "Failed to fetch new incident data: " + err.message;
    if (setErrorDialog) {
      setErrorDialog({
        open: true,
        message: errorMessage,
      });
    } else {
      alert(errorMessage);
    }
    setNewIncident({ ...IncidentModel });
  }
};

export const createIncident = async (incidentData) => {
  const endpoint = "/admin/incidentEntry/create.json";
  try {
    const dataToSend = { ...incidentData };
    delete dataToSend.id;
    delete dataToSend.externalId;
    delete dataToSend.sourceId;
    delete dataToSend.state;
    delete dataToSend.locations;
    delete dataToSend.maxLaneCount;
    delete dataToSend.locationResolved;
    delete dataToSend.locationResolutionStatus;
    delete dataToSend.lastUpdateTime;
    delete dataToSend.dataValidationStatus;

    const res = await fetch(process.env.REACT_APP_API_HOST + endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(dataToSend),
    });
    const responseData = await res.json();

    if (responseData.success) {
      alert("New Incident is Created");
      return { success: true };
    } else {
      const errorMessage = responseData.error?.message || "Unknown error";
      alert(errorMessage);
      return { success: false, error: errorMessage };
    }
  } catch (err) {
    alert(err.message);
    return { success: false, error: err.message };
  }
};

export const updateIncident = async (incidentId, incidentData) => {
  const endpoint = "/admin/incidentEntry/" + incidentId + "/update.json";
  try {
    const dataToSend = { ...incidentData };
    if (dataToSend.locations?.length > 0) {
      dataToSend.locationDescription = dataToSend.locations[0].description;
    }
    // Round-trip the resolution state loaded from the server so the backend
    // does not have to re-resolve (issue #13)
    if (!dataToSend.resolvedLocation && dataToSend.locations?.length > 0) {
      dataToSend.resolvedLocation =
        dataToSend.locations[0].resolvedLocation || null;
    }
    if (
      dataToSend.createUnresolved === undefined ||
      dataToSend.createUnresolved === null
    ) {
      dataToSend.createUnresolved =
        dataToSend.locationResolutionStatus === "LOCATION_UNRESOLVABLE_AUTO";
    }
    delete dataToSend.id;
    delete dataToSend.externalId;
    delete dataToSend.sourceId;
    delete dataToSend.state;
    delete dataToSend.locations;
    delete dataToSend.maxLaneCount;
    delete dataToSend.locationResolved;
    delete dataToSend.lastUpdateTime;
    delete dataToSend.locationResolutionStatus;
    delete dataToSend.closedTime;
    delete dataToSend.dataValidationStatus;
    const res = await fetch(process.env.REACT_APP_API_HOST + endpoint, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(dataToSend),
    });
    const responseData = await res.json();

    if (responseData.success) {
      alert("Incident is Updated");
      return { success: true };
    } else {
      const errorMessage = responseData.error?.message || "Unknown error";
      alert(errorMessage);
      return { success: false };
    }
  } catch (err) {
    alert(err.message);
    return { success: false, error: err.message };
  }
};

export const closeIncident = async (incidentId, incidentData) => {
  const endpoint = "/admin/incidentEntry/" + incidentId + "/close.json";
  try {
    const res = await fetch(process.env.REACT_APP_API_HOST + endpoint, {
      method: "PUT",
    });
    res.json().then((res) => {
      if (res.success) alert("Incident is Closed");
      else alert(res.error.message);
    });
  } catch (err) {
    alert(err.message);
  }
};
